/**
 * web/src/lib/shareTarget.ts — the PWA share target (card jZn7vpkB7EG1).
 *
 * Android's share sheet can hand Pear a title / text / url triple straight
 * from any other app (`share_target` in web/public/manifest.json → GET
 * /share?title=…&text=…&url=…). This module owns the client half of that
 * contract:
 *
 *   capture — main.tsx calls captureSharePath() BEFORE React mounts: it reads
 *             the three params off /share, parks them in sessionStorage (a
 *             per-tab key, so it survives whatever hop sign-up takes in either
 *             direction), and rewrites the address bar back to "/". It never
 *             fetches and never POSTs — an arrived share is a draft, not a
 *             save.
 *   read    — App reads it once at mount and holds it until the session is a
 *             real couple ("chat"), because the pair screen has nowhere to put
 *             a capture bar. A share received signed-out therefore survives
 *             sign-up instead of being silently dropped.
 *   apply   — Notebook fills its capture bar with the words and pins the type:
 *             a URL that parses → link, a url param that will NOT parse →
 *             plain note (the card's fallback), no url at all → let the bar's
 *             own detection decide. Save stays the user's tap.
 *
 * localStorage is deliberately NOT used: a share is a one-launch intent, and a
 * stale payload must never re-fill the bar next week.
 */
export interface PearShare {
  title: string;
  text: string;
  url: string;
}

/** The one path the manifest points at. No trailing slash, no /index.html. */
const SHARE_PATH = '/share';
const SS_KEY = 'pear.share.pending';
/** A whole value that is a URL — a bare "not-a-url" or "htp://x" must not pass. */
const URL_OK = /^https?:\/\/\S+$/i;

const normalisePath = (pathname: string): string =>
  pathname.replace(/\/+$/, '') || '/';

/**
 * Boot half: /share → park the payload, put the address bar back to "/".
 * Returns the parked share, or null when this is not a share launch (or the
 * share carried nothing at all). Safe to call on every boot.
 */
export function captureSharePath(pathname: string): PearShare | null {
  if (normalisePath(pathname) !== SHARE_PATH) return null;
  const params = new URLSearchParams(window.location.search);
  const share: PearShare = {
    title: (params.get('title') ?? '').trim(),
    text: (params.get('text') ?? '').trim(),
    url: (params.get('url') ?? '').trim(),
  };
  const hasWords = Boolean(share.title || share.text || share.url);
  try {
    if (hasWords) window.sessionStorage.setItem(SS_KEY, JSON.stringify(share));
    else window.sessionStorage.removeItem(SS_KEY);
  } catch {
    /* storage blocked: the share is dropped, the boot is not */
  }
  window.history.replaceState(null, '', '/');
  return hasWords ? share : null;
}

/** The parked share, still parked — reading does not consume it. */
export function readPendingShare(): PearShare | null {
  try {
    const raw = window.sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PearShare>;
    const share: PearShare = {
      title: String(parsed.title ?? ''),
      text: String(parsed.text ?? ''),
      url: String(parsed.url ?? ''),
    };
    if (!share.title && !share.text && !share.url) return null;
    return share;
  } catch {
    return null;
  }
}

/** Drop the parked share — called by the panel the moment it has applied it. */
export function clearPendingShare(): void {
  try {
    window.sessionStorage.removeItem(SS_KEY);
  } catch {
    /* storage blocked: nothing to clear */
  }
}

/**
 * What the capture bar opens with: the words (title, text and url, de-duped —
 * the OS routinely sets two of the three to the same string) and the type to
 * pin. `type: null` means "let the bar's own detection decide".
 */
export function shareCapture(share: PearShare): { body: string; type: 'link' | 'note' | null } {
  const parts = [share.title, share.text, share.url]
    .map(part => part.trim())
    .filter((part, index, all) => part !== '' && all.indexOf(part) === index);
  const body = parts.join('\n');
  if (!body) return { body: '', type: null };
  if (URL_OK.test(share.url.trim())) return { body, type: 'link' };
  /* A url param that will not parse falls back to a plain note (card
     acceptance) — pinned rather than left to detection, so a price inside the
     shared text cannot read the share as a gift. */
  if (share.url.trim() !== '') return { body, type: 'note' };
  return { body, type: null };
}
